import { getData, postData, putData } from "actions";
import { CKEditor } from "ckeditor4-react";
import { CardContainer, Container } from "components";
import Breadcrumb from "components/molecules/Breadcrumb";
import { API_URL_kbArticle, API_URL_kbCategory } from "constants/api";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { kbArticleReducers } from "redux/kbArticleSlice";
import { kbCategoryReducers } from "redux/kbCategorySlice";
import moment from "moment";
import { AuthContext } from "context/AuthContext";
import { AiOutlinePlus } from "react-icons/ai";
import ModalAddCategory from "components/molecules/ModalAddCategory";

const KBFormArticle = ({ typeForm }) => {
  const location = useLocation();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRefs = useRef([]);
  const [modalCategory, setModalCategory] = useState(false);

  const { getKbCategoryResult } = useSelector((state) => ({
    getKbCategoryResult: state.kbCategory.getKbCategoryResult,
  }));

  const formik = useFormik({
    initialValues: {
      name: location.state ? location.state.item.name : "",
      status: location.state ? location.state.item.status : 0,
      type: location.state ? location.state.item.type : "",
      publish_time: location.state ? location.state.item.publish_time : false,
      category_id: location.state ? location.state.item.category_id : "",
      description: location.state ? location.state.item.description : "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      category_id: Yup.string().required("Category is required"),
      description: Yup.string().required("Description is required"),
      publish_time: Yup.string().required("Publish Time is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_kbArticle,
          formData,
          { dispatch, redux: kbArticleReducers },
          "ADD_KBARTICLE",
          () => {
            navigate("/administrator/knowledgebase/question");
          }
        );
      } else {
        putData(
          `${API_URL_kbArticle}${location.state.item.id}/`,
          formData,
          { dispatch, redux: kbArticleReducers },
          "ADD_KBARTICLE",
          () => {
            navigate("/administrator/knowledgebase/question");
          }
        );
      }
    },
  });

  const getKbCategory = useCallback(
    async (params) => {
      getData(
        API_URL_kbCategory,
        params,
        { dispatch, redux: kbCategoryReducers },
        "GET_KBCATEGORY"
      );
    },
    [dispatch]
  );

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };
  useEffect(() => {
    const params = `?limit=${9999999}`;
    getKbCategory(params);
  }, []);

  const callbackCategory = () => {
    const params = `?limit=${9999999}`;
    getKbCategory(params);
  };

  useEffect(() => {
    if (typeForm === "create") {
      formik.setFieldValue("name", "");
      formik.setFieldValue("status", 0);
      formik.setFieldValue("type", 0);
      formik.setFieldValue("description", "");
      formik.setFieldValue("publish_time", "");
      formik.setFieldValue("category_id", "");
    }
  }, [typeForm]);

  const closeModal = () => {
    setModalCategory(false);
  };

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Form Article"}
        excludedPaths={["knowledgebase", "administrator"]}
        group={"Superadmin"}
      />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-8">
          <CardContainer
            title={typeForm === "create" ? "Add Article" : "Edit Article"}
          >
            <div className="m-5 flex flex-col gap-2">
              <label>
                Name
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                id="name"
                onChange={formik.handleChange}
                ref={(ref) => (inputRefs.current[0] = ref)}
                value={formik.values.name}
                className="p-1 w-full md:w-96 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              {formik.touched.name && formik.errors.name && (
                <div className="text-red-500 dark:text-red-300 text-xs">
                  {formik.errors.name}
                </div>
              )}
            </div>

            <div className="m-5 flex flex-col">
              <label>
                Description
                <span className="text-red-500">*</span>
              </label>

              <CKEditor
                config={toolbarConfig}
                type="classic"
                initData={formik.values.description}
                onChange={(e) => {
                  formik.setFieldValue("description", e.editor.getData());
                }}
              />
              {formik.touched.description && formik.errors.description && (
                <div className="text-red-500 dark:text-red-300 text-xs">
                  {formik.errors.description}
                </div>
              )}
            </div>
          </CardContainer>
        </div>
        <div className="col-span-12 md:col-span-4">
          <form onSubmit={handleFormSubmit}>
            <CardContainer title={"Publish"}>
              <div className="mx-4 py-2 align-middle items-center">
                <label className="block font-bold mb-2">Status</label>
                <label className="inline-flex items-center ml-1">
                  <input
                    id="status"
                    name="status"
                    type="radio"
                    checked={formik.values.status === 1}
                    onChange={() => formik.setFieldValue("status", 1)}
                    ref={(ref) => (inputRefs.current[1] = ref)}
                    className="w-4 h-4 rounded-full"
                  />
                  <span className="ml-2">Published</span>
                </label>
                <label className="inline-flex items-center ml-1">
                  <input
                    id="status"
                    name="status"
                    type="radio"
                    checked={formik.values.status === 0}
                    onChange={() => formik.setFieldValue("status", 0)}
                    ref={(ref) => (inputRefs.current[2] = ref)}
                    className="lg:ml-2 w-4 h-4 rounded-full"
                  />
                  <span className="ml-2">Draft</span>
                </label>
              </div>

              <div className="mx-4 py-2 align-middle items-center">
                <label className="block font-bold mb-2">Visibility</label>
                <label className="inline-flex items-center ml-1">
                  <input
                    id="type"
                    name="type"
                    type="radio"
                    checked={formik.values.type === 1}
                    onChange={() => formik.setFieldValue("type", 1)}
                    className="w-4 h-4 rounded-full"
                  />
                  <span className="ml-2">Published</span>
                </label>
                <label className="inline-flex items-center ml-1">
                  <input
                    id="type"
                    name="type"
                    type="radio"
                    checked={formik.values.type === 0}
                    onChange={() => formik.setFieldValue("type", 0)}
                    className="lg:ml-2 w-4 h-4 rounded-full"
                  />
                  <span className="ml-2">Draft</span>
                </label>
              </div>

              <div className="mx-4 py-2 align-middle items-center">
                <label className="block font-bold mb-2">
                  Publish immediately <span className="text-red-500">*</span>
                </label>
                <div className="inline-flex items-center ml-1">
                  <input
                    type="datetime-local"
                    id="publish_time"
                    name="publish_time"
                    value={moment(
                      formik.values.publish_time,
                      "YYYY-MM-DD HH:mm"
                    ).format("YYYY-MM-DDTHH:mm")}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[3] = ref)}
                    className="p-1 w-full border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
                {formik.touched.publish_time && formik.errors.publish_time && (
                  <div className="text-red-500 dark:text-red-300 text-xs">
                    {formik.errors.publish_time}
                  </div>
                )}
              </div>
            </CardContainer>
            <div className="mb-4"></div>
            <div
              required={true}
              className="bg-neutral overflow-hidden dark:bg-neutral-800 rounded shadow"
            >
              <div
                className={`h-12 w-full flex justify-between items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600`}
              >
                <span>Category</span>
                <button
                  onClick={() => setModalCategory(true)}
                  type="button"
                  className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
                >
                  <AiOutlinePlus /> Add
                </button>
              </div>

              <div className="mx-4 py-2 align-middle items-center overflow-y-auto h-28">
                {getKbCategoryResult.results &&
                  getKbCategoryResult.results.map((item, index) => (
                    <label key={index} className="flex items-center">
                      <input
                        type="radio"
                        className="form-radio mr-2"
                        name="category_id"
                        id="category_id"
                        checked={formik.values.category_id === item.id}
                        onChange={() =>
                          formik.setFieldValue("category_id", item.id)
                        }
                      />
                      {item.name}
                    </label>
                  ))}
              </div>
              {formik.touched.category_id && formik.errors.category_id && (
                <div className="text-red-500 dark:text-red-300 text-xs">
                  {formik.errors.category_id}
                </div>
              )}
              <div className="h-14 w-full flex items-center px-5 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
                <button
                  type="submit"
                  className="rounded-md text-white py-1.5 w-16 bg-sky-600 hover:bg-sky-800 "
                >
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ModalAddCategory
        isOpen={modalCategory}
        onClose={closeModal}
        callback={() => callbackCategory()}
      />
    </Container>
  );
};

export default KBFormArticle;
